import {useForm} from 'react-hook-form';
import {useId, useState} from 'react';
import {User} from '../../../user';
import {AccountSettingsPanel} from '../account-settings-panel';
import {Button} from '@common/ui/buttons/button';
import {Form} from '@common/ui/forms/form';
import {FormTextField} from '@common/ui/forms/input-field/text-field/text-field';
import {Trans} from '@common/i18n/trans';
import {AccountSettingsId} from '@common/auth/ui/account-settings/account-settings-sidenav';
import {useMutation} from '@tanstack/react-query';
import {apiClient, queryClient} from '@common/http/query-client';
import {toast} from '@common/ui/toast/toast';
import {message} from '@common/i18n/message';

interface DonationFormData {
  cardpointe_username: string;
  cardpointe_password: string;
  merchant_id: string;
  merchant_site: string;
}

interface SaveDonationResponse {
  uuid: string;
  status: string;
}

function useSaveDonationSettings(form: ReturnType<typeof useForm<DonationFormData>>) {
  return useMutation({
    mutationFn: (data: DonationFormData) =>
      apiClient.post<SaveDonationResponse>('donation/save-settings', data).then(r => r.data),
    onSuccess: () => {
      toast(message('Donation settings saved successfully'));
      queryClient.invalidateQueries({queryKey: ['users']});
    },
    onError: () => {
      toast.danger(message('Failed to save donation settings'));
    },
  });
}

interface Props {
  user: User;
}

export function DonationPanel({user}: Props) {
  const formId = useId();
  const [savedUuid, setSavedUuid] = useState<string | null>(user.uuid || null);

  const form = useForm<DonationFormData>({
    defaultValues: {
      cardpointe_username: user.cardpointe_username || '',
      cardpointe_password: user.cardpointe_password || '',
      merchant_id: user.merchant_id || '',
      merchant_site: user.merchant_site || '',
    },
  });

  const saveMutation = useSaveDonationSettings(form);

  const embedCode = savedUuid
    ? `<div id="propelrpay-card" data-ar-id="RjWwYFZupv"></div>

<script src="https://sites.californiawebcompany.com/propelrpay/widget/cardpointe.js?v=8" data-uuid="${savedUuid}"></script>`
    : null;

  const copyEmbedCode = async () => {
    if (embedCode) {
      try {
        await navigator.clipboard.writeText(embedCode);
        toast(message('Embed code copied to clipboard'));
      } catch (error) {
        toast.danger(message('Failed to copy embed code'));
      }
    }
  };

  // Only show for users with "users" role
  const isRegularUser = user.roles?.some(role => role.name === 'users');
  if (!isRegularUser) {
    return null;
  }

  return (
    <AccountSettingsPanel
      id={AccountSettingsId.Donation}
      title={<Trans message="Donation Widget Setup" />}
      actions={
        <Button
          type="submit"
          variant="flat"
          color="primary"
          form={formId}
          disabled={saveMutation.isPending || !form.formState.isValid}
        >
          {saveMutation.isPending ? (
            <Trans message="Saving..." />
          ) : (
            <Trans message="Save" />
          )}
        </Button>
      }
    >
      {/* Information Section */}
      <div className="mb-24 rounded-lg bg-alt p-16">
        <h3 className="mb-12 font-semibold">
          <Trans message="How to Get Your CardPointe Credentials" />
        </h3>
        <div className="space-y-8 text-sm text-muted">
          <p>
            <Trans message="To set up the donation widget, you'll need your CardPointe merchant credentials:" />
          </p>
          <ol className="ml-16 list-decimal space-y-4">
            <li>
              <strong><Trans message="Sign Up:" /></strong>{' '}
              <Trans message="Visit" />{' '}
              <a
                href="https://cardconnect.com/cardpointe"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline"
              >
                CardConnect/CardPointe
              </a>{' '}
              <Trans message="for a merchant account." />
            </li>
            <li>
              <strong><Trans message="Get Credentials:" /></strong>{' '}
              <Trans message="Log in to your merchant portal and go to Settings > API Credentials." />
            </li>
            <li>
              <strong><Trans message="Merchant Site:" /></strong>{' '}
              <Trans message="Usually 'fts' or 'fts-uat' (testing). Contact CardPointe if unsure." />
            </li>
          </ol>
        </div>
      </div>

      {/* Form Section */}
      <Form
        form={form}
        id={formId}
        onSubmit={data => {
          saveMutation.mutate(data, {
            onSuccess: response => {
              setSavedUuid(response.uuid);
            },
          });
        }}
      >
        <div className="grid gap-24 md:grid-cols-2">
          <FormTextField
            name="cardpointe_username"
            label={<Trans message="CardPointe Username" />}
            required
          />
          <FormTextField
            name="cardpointe_password"
            label={<Trans message="CardPointe Password" />}
            type="password"
            required
          />
          <FormTextField
            name="merchant_id"
            label={<Trans message="Merchant ID" />}
            required
          />
          <FormTextField
            name="merchant_site"
            label={<Trans message="Merchant Site" />}
            placeholder="e.g., fts or fts-uat"
            required
          />
        </div>
      </Form>

      {/* Embed Code Section */}
      {savedUuid && (
        <div className="mt-24 rounded-lg border p-16">
          <h3 className="mb-12 font-semibold">
            <Trans message="Embed Code" />
          </h3>
          <p className="mb-12 text-sm text-muted">
            <Trans message="Copy this code and paste it into your website where you want the donation form to appear:" />
          </p>
          <div className="relative">
            <pre className="whitespace-pre-wrap break-all rounded bg-alt p-12 pr-70 text-xs">
              <code>{embedCode}</code>
            </pre>
            <Button
              variant="flat"
              color="primary"
              size="xs"
              className="absolute right-8 top-8"
              onClick={copyEmbedCode}
            >
              <Trans message="Copy" />
            </Button>
          </div>
        </div>
      )}
    </AccountSettingsPanel>
  );
}
